import axios, { AxiosInstance } from "axios"
import type { SigningAssistantFormSnapshot } from "./signingAssistantFormSnapshot"

export type SigningAssistantMessage = {
  role: "user" | "assistant" | "system"
  content: string
  created_at?: string
  message_id?: string
  seq?: number
}

/** Токен в query, чтобы не требовать CORS для X-Signing-Assistant-Token в браузере */
const TOKEN_PARAM = "signing_assistant_token"
/** Разделение историй по браузеру/устройству */
const CLIENT_KEY_PARAM = "signing_assistant_client_key"

function baseClient(): AxiosInstance {
  return axios.create({
    baseURL: process.env.REACT_APP_API_URL,
  })
}

function withToken(
  accessToken: string,
  clientKey: string,
  params?: Record<string, string | number | boolean | undefined>
): Record<string, string | number | boolean | undefined> {
  return { [TOKEN_PARAM]: accessToken, [CLIENT_KEY_PARAM]: clientKey, ...params }
}

export async function listThreads(
  contractId: string,
  accessToken: string,
  clientKey: string
): Promise<{ thread_id: string; topic: string; preview: string }[]> {
  const { data } = await baseClient().get(
    `/contracts/${contractId}/signing-assistant/threads`,
    { params: withToken(accessToken, clientKey) }
  )
  return (data?.threads || []).map((t: any) => ({
    thread_id: t.thread_id,
    topic: t.topic,
    preview: t.preview || "",
  }))
}

export async function createThread(
  contractId: string,
  accessToken: string,
  clientKey: string
): Promise<{ thread_id: string; messages: SigningAssistantMessage[]; ai_status: string }> {
  const { data } = await baseClient().post(
    `/contracts/${contractId}/signing-assistant/threads`,
    {},
    { params: withToken(accessToken, clientKey) }
  )
  return data
}

export async function getThread(
  contractId: string,
  accessToken: string,
  clientKey: string,
  threadId: string
): Promise<{
  messages: SigningAssistantMessage[]
  ai_status: string
  topic: string
}> {
  const { data } = await baseClient().get(
    `/contracts/${contractId}/signing-assistant/threads/${threadId}`,
    { params: withToken(accessToken, clientKey) }
  )
  return data
}

export async function postMessage(
  contractId: string,
  accessToken: string,
  clientKey: string,
  threadId: string,
  content: string,
  formSnapshot?: SigningAssistantFormSnapshot | null
): Promise<{ messages: SigningAssistantMessage[]; ai_status: string }> {
  const body: Record<string, unknown> = { content }
  if (formSnapshot) {
    body.form_snapshot = formSnapshot
  }
  const { data } = await baseClient().post(
    `/contracts/${contractId}/signing-assistant/threads/${threadId}/messages`,
    body,
    { params: withToken(accessToken, clientKey) }
  )
  return data
}

export async function fetchUnreadCount(
  contractId: string,
  accessToken: string,
  clientKey: string,
  lastSeenSeq: number
): Promise<number> {
  const { data } = await baseClient().get(
    `/contracts/${contractId}/signing-assistant/unread-count`,
    { params: withToken(accessToken, clientKey, { last_seen_seq: lastSeenSeq }) }
  )
  return typeof data?.unread_count === "number" ? data.unread_count : 0
}
